import { useCallback, useEffect, useRef, useState } from 'react';
import SocketProgressBar from '@/app/common/components/SocketProgressBar';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import {
  forgetPromptRunBatch,
  invalidatePromptLists,
  readPromptRunBatch,
  subscribePromptRunBatch,
  subscribePromptRunCleared,
  trackerAiClient as api,
} from '@/app/TrackerAi/api/trackerAiClient';
import { batchRunPercent, isTerminalPromptRunBatch } from '../../utils/prompt-run';
import { useProject } from '../../context/ProjectContext';

const BATCH_POLL_MS = 8000;

/** One bar for every prompt run started from the tracker (list, topic, create). */
export function PromptRunProgress({ onComplete }: { onComplete?: () => void }) {
  const { refresh, setError } = useProject();
  const [batchId, setBatchId] = useState<string | null>(null);
  const [initialPercentage, setInitialPercentage] = useState<number | undefined>();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const setErrorRef = useRef(setError);
  setErrorRef.current = setError;

  const finish = useCallback((message?: string) => {
    if (message) setErrorRef.current(message);
    invalidatePromptLists();
    void refreshRef.current({ silent: true });
    onCompleteRef.current?.();
    forgetPromptRunBatch();
    setBatchId(null);
    setInitialPercentage(undefined);
  }, []);

  useEffect(
    () =>
      subscribePromptRunBatch((id) => {
        setInitialPercentage(undefined);
        setBatchId(id);
      }),
    [],
  );

  useEffect(
    () =>
      subscribePromptRunCleared(() => {
        setBatchId(null);
        setInitialPercentage(undefined);
        invalidatePromptLists();
        void refreshRef.current({ silent: true });
      }),
    [],
  );

  useEffect(() => {
    const stored = readPromptRunBatch();
    if (!stored) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await api.getPromptRunBatch(stored);
        if (cancelled) return;
        if (isTerminalPromptRunBatch(status.status)) {
          finish();
          return;
        }
        setInitialPercentage(batchRunPercent(status));
        setBatchId(stored);
      } catch {
        if (!cancelled) setBatchId(stored);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [finish]);

  useEffect(() => {
    if (!batchId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await api.getPromptRunBatch(batchId);
        if (cancelled) return;
        if (isTerminalPromptRunBatch(status.status)) {
          finish();
          return;
        }
        setInitialPercentage(batchRunPercent(status));
      } catch {
        /* keep the bar; user can stop from the prompt menu */
      }
    };
    const timer = window.setInterval(() => {
      void poll();
    }, BATCH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [batchId, finish]);

  const handleComplete = useCallback(
    (data?: CentrifugeProgressMessage) => {
      finish(data?.message);
    },
    [finish],
  );

  if (!batchId) return null;

  return (
    <div className="mb-3">
      <SocketProgressBar
        key={batchId}
        channelId={batchId}
        active
        initialPercentage={initialPercentage}
        description="در حال اجرای پرامپت و پیدا کردن برندها..."
        onComplete={handleComplete}
      />
    </div>
  );
}
